import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Autocomplete,
  TextField,
  Fade,
} from "@mui/material";
import { Close, Save, UploadFile } from "@mui/icons-material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Import Leaflet components
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  useMap,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

// Fix for default marker icon
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

import DynamicField from "../Dynamic/DynamicField";
import DynamicOffcanvas from "../Dynamic/DynamicOffcanvas";
import { PostApi, PutApi, GetApi } from "../../Services/api/ApiCall";
import API_URLS from "../../Services/api/action";
import { useUser } from "../../Context/UserContext";
import DynamicModal from "../Dynamic/DynamicModal";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: icon,
  shadowUrl: iconShadow,
});

// Component to update map center and zoom
const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && !isNaN(center.lat) && !isNaN(center.lng)) {
      map.flyTo([center.lat, center.lng], 15, { duration: 1.5 });
    }
  }, [center, map]);
  return null;
};

// LocationMarker Component - handles map clicks
const LocationMarker = ({ onLocationChange }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      onLocationChange(lat.toFixed(6), lng.toFixed(6), true); // Pass true to indicate map click
    },
  });
  return null;
};

const RegistrationForm = ({
  open,
  onClose,
  productionTypes,
  productionCenters,
  selectedRegistration,
  actionType,
  onSuccess,
}) => {
  // Form State
  const [formData, setFormData] = useState({
    department: "",
    production_type: "government",
    name_of_production_centre: "",
    area_acres: "",
    complete_address: "",
    district: "",
    block: "",
    village: "",
    contact_person: "",
    mobile_number: "",
    latitude: "",
    longitude: "",
    nursery_category: "",
    nursery_capacity: "",
    certification_details: "",
    certificate_file: null,
    status: "pending",
  });

  const [errors, setErrors] = useState({});
  const [mapCenter, setMapCenter] = useState({ lat: 11.1271, lng: 78.6569 });
  const [loading, setLoading] = useState(false);
  const [geoCodingLoading, setGeoCodingLoading] = useState(false);
  const [addTypeDialogOpen, setAddTypeDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [addTypeError, setAddTypeError] = useState("");
  const { token, authData } = useUser();

  // State for Master Data
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [blockOptions, setBlockOptions] = useState([]);
  const [villageOptions, setVillageOptions] = useState([]);

  // Ref for debouncing
  const addressDebounceRef = useRef(null);
  const fileInputRef = useRef(null);

  // Flag to prevent auto-geocoding when address is updated by Map Click
  const isMapClickRef = useRef(false);

  const nurseryCategoryOptions = [
    { id: "High", name: "Hi-Tech" },
    { id: "Medium", name: "Big" },
    { id: "Small", name: "Small" },
    { id: "Tissue Culture", name: "Tissue Culture" },
  ];

  const productionTypeOptions = [
    { id: "government", name: "Government" },
    { id: "private", name: "Private" },
  ];

  const customColors = {
    primary: "#ffc022",
    success: "#005500",
    warning: "#ff9933",
    white: "#ffffff",
    black: "#000000",
  };

  // --- Fetch Initial Data ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [deptRes, distRes] = await Promise.all([
          GetApi(`/officers/departments`, token),
          GetApi(`${API_URLS.GetDistricts}`, token),
        ]);
        setDepartmentOptions(deptRes || []);
        setDistrictOptions(distRes || []);
      } catch (error) {
        console.error("Error fetching initial data", error);
        toast.error("Failed to load initial data");
      }
    };

    if (open && token) fetchData();
  }, [open, token]);

  // --- ENHANCED COMBINED AUTO-GEOCODING LOGIC ---
  useEffect(() => {
    if (addressDebounceRef.current) {
      clearTimeout(addressDebounceRef.current);
    }

    // If the address was just updated by clicking the map, DON'T geocode again
    if (isMapClickRef.current) {
      isMapClickRef.current = false;
      return;
    }

    // Get text names for dropdowns to build a complete search string
    const districtObj = districtOptions.find((d) => d.id == formData.district);
    const blockObj = blockOptions.find((b) => b.id == formData.block);
    const villageObj = villageOptions.find((v) => v.id == formData.village);

    const addressText = formData.complete_address || "";
    const districtText = districtObj?.District_Name || "";
    const blockText = blockObj?.Block_Name || "";
    const villageText = villageObj?.Village_Name || "";

    // Only search if we have at least some location info (typed address OR a dropdown selected)
    const hasData =
      addressText.trim().length > 3 || districtText || blockText || villageText;

    if (hasData) {
      setGeoCodingLoading(true);

      addressDebounceRef.current = setTimeout(() => {
        // Combine all parts intelligently (avoiding duplicates)
        let combinedQuery =
          villageText || blockText || districtText || addressText;
        if (addressText && addressText !== villageText) {
          combinedQuery =
            `${villageText}, ${blockText}, ${districtText}, ${addressText}`
              .replace(/,\s*,/g, ",")
              .replace(/^,\s*/, "");
        }

        handleGeocodeSearch(combinedQuery);
      }, 1500); // 1.5s debounce
    } else {
      setGeoCodingLoading(false);
    }

    return () => {
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    };
  }, [
    formData.complete_address,
    formData.district,
    formData.block,
    formData.village,
    districtOptions,
    blockOptions,
    villageOptions,
  ]);

  // --- Reset/Populate Form on Open ---
  useEffect(() => {
    if (open) {
      if (actionType === "edit" && selectedRegistration) {
        const editData = {
          department: selectedRegistration.department_id || "",
          production_type: selectedRegistration.production_type || "government",
          name_of_production_centre:
            selectedRegistration.name_of_production_centre || "",
          area_acres: selectedRegistration.area_acres || "",
          complete_address: selectedRegistration.complete_address || "",
          district: selectedRegistration.district_id || "",
          block: selectedRegistration.block_id || "",
          village: selectedRegistration.village_id || "",
          contact_person: selectedRegistration.contact_person || "",
          mobile_number: selectedRegistration.mobile_number || "",
          latitude: selectedRegistration.latitude || "",
          longitude: selectedRegistration.longitude || "",
          nursery_category: selectedRegistration.nursery_category || "",
          nursery_capacity: selectedRegistration.nursery_capacity || "",
          certification_details:
            selectedRegistration.certification_details || "",
          certificate_file: null,
          status: selectedRegistration.status || "pending",
        };
        setFormData(editData);

        if (selectedRegistration.latitude && selectedRegistration.longitude) {
          const lat = parseFloat(selectedRegistration.latitude);
          const lng = parseFloat(selectedRegistration.longitude);
          if (!isNaN(lat) && !isNaN(lng)) {
            setMapCenter({ lat, lng });
          }
        }

        const loadDependencies = async () => {
          if (selectedRegistration.district_id) {
            try {
              const blockRes = await GetApi(
                `${API_URLS.GetBlocks}?district_id=${selectedRegistration.district_id}`,
                token,
              );
              setBlockOptions(blockRes || []);
              if (selectedRegistration.block_id) {
                const villageRes = await GetApi(
                  `${API_URLS.GetVillages}?block_id=${selectedRegistration.block_id}`,
                  token,
                );
                setVillageOptions(villageRes || []);
              }
            } catch (e) {
              console.error(e);
            }
          }
        };
        loadDependencies();
      } else {
        resetForm();
      }
    }
  }, [open, actionType, selectedRegistration, token]);

  const resetForm = () => {
    setFormData({
      department: "",
      production_type: "government",
      name_of_production_centre: "",
      area_acres: "",
      complete_address: "",
      district: "",
      block: "",
      village: "",
      contact_person: "",
      mobile_number: "",
      latitude: "",
      longitude: "",
      nursery_category: "",
      nursery_capacity: "",
      certification_details: "",
      certificate_file: null,
      status: "pending",
    });
    setErrors({});
    setBlockOptions([]);
    setVillageOptions([]);
    setMapCenter({ lat: 11.1271, lng: 78.6569 });
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  // --- Geocoding Helpers ---

  const reverseGeocodeLeaflet = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      );
      const result = await response.json();
      if (result && result.display_name) {
        isMapClickRef.current = true; // Set flag BEFORE updating state
        setFormData((prev) => ({
          ...prev,
          complete_address: result.display_name,
        }));
      }
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
    }
  };

  const handleGeocodeSearch = async (addressString) => {
    if (!addressString || addressString.trim().length < 3) {
      setGeoCodingLoading(false);
      return;
    }

    const searchQuery = addressString.toLowerCase().includes("tamil nadu")
      ? addressString
      : `${addressString}, Tamil Nadu, India`;

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
      );
      const results = await response.json();

      if (results.length > 0) {
        const { lat, lon } = results[0];
        const newLat = parseFloat(lat);
        const newLng = parseFloat(lon);

        setFormData((prev) => ({
          ...prev,
          latitude: newLat,
          longitude: newLng,
        }));
        setMapCenter({ lat: newLat, lng: newLng });
      } else {
        console.warn("Location not found for:", searchQuery);
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
    } finally {
      setGeoCodingLoading(false);
    }
  };

  // Updated to accept 'isMapClick' parameter
  const handleLeafletLocationChange = (lat, lng, isMapClick = false) => {
    if (isMapClick) isMapClickRef.current = true; // Set flag for map clicks

    setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
    setMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
    reverseGeocodeLeaflet(lat, lng);
  };

  const handleMarkerDrag = (e) => {
    const position = e.target.getLatLng();
    handleLeafletLocationChange(
      position.lat.toFixed(6),
      position.lng.toFixed(6),
      true,
    ); // Set true for drag too
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    },
    [errors],
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed.");
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }
    if (file.size > 409600) {
      toast.error("File size must be less than 400KB.");
      if (fileInputRef.current) fileInputRef.current.value = null;
      return;
    }
    setFormData((prev) => ({ ...prev, certificate_file: file }));
    toast.success("File selected successfully.");
  };

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = [
      "department",
      "production_type",
      "name_of_production_centre",
      "complete_address",
      "district",
      "block",
      "village",
      "contact_person",
      "mobile_number",
      "latitude",
      "longitude",
      "nursery_category",
      "nursery_capacity",
      "area_acres",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] =
          `${field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())} is required`;
      }
    });

    if (formData.mobile_number && !/^\d{10}$/.test(formData.mobile_number)) {
      newErrors.mobile_number = "Mobile number must be 10 digits";
    }

    if (!formData.latitude || !formData.longitude) {
      newErrors.map =
        "Location could not be determined. Please check address or select on map.";
    }

    if (formData.production_type === "private") {
      if (!formData.certification_details)
        newErrors.certification_details =
          "Certification details required for private type";
      if (!formData.certificate_file && actionType !== "edit")
        newErrors.certificate_file =
          "Certification document (PDF) is required for private type";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0)
      toast.warn("Please fill all required fields correctly.");
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const submitData = new FormData();

    submitData.append("department_id", formData.department);
    submitData.append("district_id", formData.district);
    submitData.append("block_id", formData.block);
    submitData.append("village_id", formData.village);
    submitData.append(
      "name_of_production_centre",
      formData.name_of_production_centre,
    );
    submitData.append("area_acres", formData.area_acres);
    submitData.append("complete_address", formData.complete_address);
    submitData.append("contact_person", formData.contact_person);
    submitData.append("mobile_number", formData.mobile_number);
    submitData.append("latitude", formData.latitude);
    submitData.append("longitude", formData.longitude);
    submitData.append("production_type", formData.production_type);
    submitData.append("status", formData.status);
    submitData.append("nursery_category", formData.nursery_category);
    submitData.append("nursery_capacity", formData.nursery_capacity);
    submitData.append("user_name", authData?.user_name);

    if (formData.production_type === "private") {
      submitData.append(
        "certification_details",
        formData.certification_details || "",
      );
      if (formData.certificate_file)
        submitData.append("certificate_file", formData.certificate_file);
    }

    try {
      if (actionType === "edit") {
        await PutApi(
          `${API_URLS.ProductionCenter}`,
          { id: selectedRegistration.id },
          submitData,
          token,
        );
        toast.success("Production Center updated successfully!");
      } else {
        await PostApi(`${API_URLS.ProductionCenter}`, submitData, token);
        toast.success("Production Center created successfully!");
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Submit failed:", error);
      toast.error(error.response?.data?.message || "Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddProductionType = async () => {
    if (!newTypeName.trim()) {
      setAddTypeError("Type name is required");
      return;
    }
    try {
      setLoading(true);
      await PostApi(
        { name: newTypeName.trim() },
        `${API_URLS.ProductionCenterType}`,
        token,
      );
      toast.success("Production Type added successfully");
      setAddTypeDialogOpen(false);
      setNewTypeName("");
      setAddTypeError("");
      onSuccess();
    } catch (error) {
      setAddTypeError(
        error.response?.data?.message || "Failed to add production type",
      );
    } finally {
      setLoading(false);
    }
  };

  const getOptionById = (id, options) =>
    options.find((opt) => opt.id == id) || null;

  const offcanvasActions = (
    <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
      <Button
        variant="outlined"
        fullWidth
        startIcon={<Close />}
        onClick={onClose}
        size="large"
        disabled={loading}
        sx={{
          color: customColors.warning,
          borderColor: customColors.warning,
          fontWeight: 600,
        }}
      >
        Cancel
      </Button>
      <Button
        variant="contained"
        fullWidth
        startIcon={
          loading ? <CircularProgress size={20} color="inherit" /> : <Save />
        }
        type="submit"
        size="large"
        onClick={handleSubmit}
        disabled={loading}
        sx={{
          bgcolor: customColors.primary,
          color: customColors.black,
          fontWeight: 700,
          boxShadow: `0 4px 12px ${customColors.primary}30`,
          "&:hover": { bgcolor: customColors.primary },
        }}
      >
        {loading
          ? "Saving..."
          : actionType === "edit"
            ? "Update Center"
            : "Create Center"}
      </Button>
    </Box>
  );

  return (
    <>
      <DynamicOffcanvas
        open={open}
        onClose={onClose}
        title={
          actionType === "edit"
            ? "Edit Production Center"
            : "New Production Center"
        }
        width="900px"
        actions={offcanvasActions}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} width="250px">
              <Autocomplete
                fullWidth
                options={departmentOptions}
                getOptionLabel={(option) =>
                  option.department_name || option.name || ""
                }
                value={getOptionById(formData.department, departmentOptions)}
                onChange={(e, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    department: newValue ? newValue.id : "",
                  }));
                  if (errors.department)
                    setErrors((prev) => ({ ...prev, department: "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    required
                    size="small"
                    error={!!errors.department}
                    helperText={errors.department}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DynamicField
                field={{
                  name: "name_of_production_centre",
                  label: "Name of Centre",
                  required: true,
                }}
                value={formData.name_of_production_centre}
                onChange={handleChange}
                error={!!errors.name_of_production_centre}
                helperText={errors.name_of_production_centre}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DynamicField
                field={{
                  name: "production_type",
                  label: "Production Type",
                  type: "select",
                  required: true,
                }}
                options={productionTypeOptions}
                value={formData.production_type}
                onChange={handleChange}
                error={!!errors.production_type}
                helperText={errors.production_type}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DynamicField
                field={{
                  name: "nursery_category",
                  label: "Nursery Category",
                  type: "select",
                  required: true,
                }}
                options={nurseryCategoryOptions}
                value={formData.nursery_category}
                onChange={handleChange}
                error={!!errors.nursery_category}
                helperText={errors.nursery_category}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DynamicField
                field={{
                  name: "nursery_capacity",
                  label: "Nursery Capacity",
                  type: "number",
                  required: true,
                }}
                value={formData.nursery_capacity}
                onChange={handleChange}
                error={!!errors.nursery_capacity}
                helperText={errors.nursery_capacity}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <DynamicField
                field={{
                  name: "area_acres",
                  label: "Area (in Acres)",
                  type: "number",
                  required: true,
                }}
                value={formData.area_acres}
                onChange={handleChange}
                error={!!errors.area_acres}
                helperText={errors.area_acres}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DynamicField
                field={{
                  name: "contact_person",
                  label: "Contact Person",
                  required: true,
                }}
                value={formData.contact_person}
                onChange={handleChange}
                error={!!errors.contact_person}
                helperText={errors.contact_person}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DynamicField
                field={{
                  name: "mobile_number",
                  label: "Mobile Number",
                  required: true,
                }}
                value={formData.mobile_number}
                onChange={handleChange}
                error={!!errors.mobile_number}
                helperText={errors.mobile_number}
              />
            </Grid>

            {/* Hierarchy Dropdowns */}
            <Grid item xs={12} md={4} width="250px">
              <Autocomplete
                fullWidth
                size="small"
                options={districtOptions}
                getOptionLabel={(option) => option.District_Name || ""}
                value={getOptionById(formData.district, districtOptions)}
                onChange={async (e, newValue) => {
                  const districtId = newValue ? newValue.id : "";
                  setFormData((prev) => ({
                    ...prev,
                    district: districtId,
                    block: "",
                    village: "",
                  }));
                  setBlockOptions([]);
                  setVillageOptions([]);
                  if (errors.district)
                    setErrors((prev) => ({ ...prev, district: "" }));
                  if (districtId) {
                    try {
                      const res = await GetApi(
                        `${API_URLS.GetBlocks}?district_id=${districtId}`,
                        token,
                      );
                      setBlockOptions(res || []);
                    } catch (err) {
                      console.error(err);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="District"
                    required
                    size="small"
                    error={!!errors.district}
                    helperText={errors.district}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4} width="250px">
              <Autocomplete
                fullWidth
                size="small"
                options={blockOptions}
                getOptionLabel={(option) => option.Block_Name || ""}
                value={getOptionById(formData.block, blockOptions)}
                onChange={async (e, newValue) => {
                  const blockId = newValue ? newValue.id : "";
                  setFormData((prev) => ({
                    ...prev,
                    block: blockId,
                    village: "",
                  }));
                  setVillageOptions([]);
                  if (errors.block)
                    setErrors((prev) => ({ ...prev, block: "" }));
                  if (blockId) {
                    try {
                      const res = await GetApi(
                        `${API_URLS.GetVillages}?block_id=${blockId}`,
                        token,
                      );
                      setVillageOptions(res || []);
                    } catch (err) {
                      console.error(err);
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Block"
                    required
                    size="small"
                    error={!!errors.block}
                    helperText={errors.block}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4} width="250px">
              <Autocomplete
                fullWidth
                size="small"
                options={villageOptions}
                getOptionLabel={(option) => option.Village_Name || ""}
                value={getOptionById(formData.village, villageOptions)}
                onChange={(e, newValue) => {
                  setFormData((prev) => ({
                    ...prev,
                    village: newValue ? newValue.id : "",
                  }));
                  if (errors.village)
                    setErrors((prev) => ({ ...prev, village: "" }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Village"
                    required
                    size="small"
                    error={!!errors.village}
                    helperText={errors.village}
                  />
                )}
              />
            </Grid>

            {/* Address & Map */}
            <Grid item xs={12} md={6}>
              <DynamicField
                field={{
                  name: "complete_address",
                  label: "Complete Address",
                  multiline: true,
                  rows: 4,
                  required: true,
                }}
                value={formData.complete_address}
                onChange={handleChange}
                error={!!errors.complete_address}
                helperText={
                  geoCodingLoading
                    ? "Searching location on map..."
                    : errors.complete_address ||
                      "Type address or select dropdowns to auto-locate"
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: customColors.primary, fontWeight: 600 }}
              >
                Pin Location on Map
              </Typography>

              {errors.map && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mb: 1, display: "block" }}
                >
                  {errors.map}
                </Typography>
              )}

              <Box sx={{ position: "relative" }}>
                {geoCodingLoading && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      zIndex: 1000,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "rgba(255,255,255,0.6)",
                      borderRadius: "8px",
                    }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                )}
                <MapContainer
                  center={mapCenter}
                  zoom={10}
                  style={{
                    height: "220px",
                    width: "100%",
                    borderRadius: "8px",
                    border: `1px solid #ddd`,
                  }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <MapUpdater center={mapCenter} />
                  {formData.latitude && formData.longitude && (
                    <Marker
                      position={[
                        parseFloat(formData.latitude),
                        parseFloat(formData.longitude),
                      ]}
                      draggable={true}
                      eventHandlers={{ dragend: handleMarkerDrag }}
                    />
                  )}
                  <LocationMarker
                    onLocationChange={handleLeafletLocationChange}
                  />
                </MapContainer>
              </Box>

              <Box display="flex" gap={2} mt={1}>
                <Typography
                  variant="caption"
                  sx={{ color: customColors.success, fontStyle: "italic" }}
                >
                  Lat: {formData.latitude || "Auto"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: customColors.success, fontStyle: "italic" }}
                >
                  Lng: {formData.longitude || "Auto"}
                </Typography>
              </Box>
            </Grid>

            {/* Conditional Private Fields */}
            {formData.production_type === "private" && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: "#fff8e1",
                    borderRadius: 2,
                    border: "1px solid #ffe082",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#ff8f00", mb: 2 }}
                  >
                    Private Certification Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <DynamicField
                        field={{
                          name: "certification_details",
                          label: "Certification Details",
                          multiline: true,
                          rows: 3,
                          required: true,
                        }}
                        value={formData.certification_details}
                        onChange={handleChange}
                        error={!!errors.certification_details}
                        helperText={errors.certification_details}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="textSecondary">
                        Upload Certificate (PDF, Max 400KB)
                      </Typography>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        startIcon={<UploadFile />}
                        sx={{
                          mt: 1,
                          height: "50px",
                          borderColor: errors.certificate_file ? "red" : "#ccc",
                        }}
                      >
                        {formData.certificate_file
                          ? formData.certificate_file.name
                          : "Choose File"}
                        <input
                          type="file"
                          ref={fileInputRef}
                          hidden
                          accept="application/pdf"
                          onChange={handleFileChange}
                        />
                      </Button>
                      {errors.certificate_file && (
                        <Typography variant="caption" color="error">
                          {errors.certificate_file}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>
      </DynamicOffcanvas>

      <DynamicModal
        open={addTypeDialogOpen}
        title={
          <Typography sx={{ color: customColors.black, fontWeight: 700 }}>
            ➕ Add New Production Type
          </Typography>
        }
        contentText={
          <Box sx={{ p: 2 }}>
            <DynamicField
              field={{
                name: "newTypeName",
                label: "Type Name",
                required: true,
              }}
              value={newTypeName}
              onChange={(e) => {
                setNewTypeName(e.target.value);
                if (addTypeError) setAddTypeError("");
              }}
              error={!!addTypeError}
              helperText={addTypeError}
            />
          </Box>
        }
        onClose={() => {
          setAddTypeDialogOpen(false);
          setNewTypeName("");
          setAddTypeError("");
        }}
        onConfirm={handleAddProductionType}
        confirmText={loading ? "Adding..." : "Add Type"}
        cancelText="Cancel"
        loading={loading}
      />
    </>
  );
};

export default RegistrationForm;
